import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

const defaultTestimonials = [
  { id: 1, name: "George Whitmore", role: "Certified HNW Investor · Harrogate", rating: 5, review: "Thorpe Dellow has paid us every single month without fail since 2017. The documentation was thorough and the team was transparent from day one." },
  { id: 2, name: "Patricia Whitmore", role: "Retired Investor · Yorkshire", rating: 5, review: "We were looking for a reliable, asset-backed income stream after retiring. This product has exceeded our expectations in every way." },
  { id: 3, name: "Richard Caldwell", role: "Sophisticated Investor · Bristol", rating: 5, review: "I invested through my IFA during Covid-19. The social housing product just kept paying throughout the entire pandemic. Exactly what it says on the tin." },
  { id: 4, name: "Sarah Pemberton", role: "Family Office · London", rating: 5, review: "The structured nature of the product, combined with first legal charge security, made this an easy decision for our family office allocation." },
  { id: 5, name: "James Hartley", role: "IFA · Manchester", rating: 5, review: "I have recommended Thorpe Dellow to several of my HNW clients. The transparency, documentation quality, and consistent distributions set them apart." },
  { id: 6, name: "Eleanor Voss", role: "Self-Certified Sophisticated Investor", rating: 5, review: "Monthly income, first charge security, and a team that actually picks up the phone. Three years in and I have no complaints whatsoever." },
];

export default function AdminTestimonials({ onSave }: Props) {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [sectionTitle, setSectionTitle] = useState("What Our Investors Say");
  const [sectionSub, setSectionSub] = useState("Feedback from certified high net worth and sophisticated investors currently participating in the Thorpe Dellow Social Housing Structured Product.");

  const update = (id: number, k: string, v: string | number) => {
    setTestimonials((prev) => prev.map((t) => t.id === id ? { ...t, [k]: v } : t));
  };

  const addTestimonial = () => {
    const newId = Math.max(...testimonials.map((t) => t.id)) + 1;
    setTestimonials((prev) => [...prev, { id: newId, name: "New Investor", role: "Investor · Location", rating: 5, review: "Enter testimonial text here." }]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Testimonials</h1>
        <p className="text-slate-500 text-sm mt-1">Manage investor testimonials shown on the homepage.</p>
      </div>

      <AdminCard title="Section Header">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
            <input value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
            <textarea value={sectionSub} onChange={(e) => setSectionSub(e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
        </div>
      </AdminCard>

      <AdminCard
        title={`Testimonials (${testimonials.length})`}
        actions={
          <button onClick={addTestimonial} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/20 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line leading-none" />
            Add
          </button>
        }
      >
        <div className="space-y-4">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-500">Testimonial #{t.id}</p>
                <button
                  onClick={() => setTestimonials((prev) => prev.filter((x) => x.id !== t.id))}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm leading-none" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Name</label>
                  <input value={t.name} onChange={(e) => update(t.id, "name", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Role / Location</label>
                  <input value={t.role} onChange={(e) => update(t.id, "role", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Rating (1–5)</label>
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} onClick={() => update(t.id, "rating", n)} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${t.rating >= n ? "text-[#C9A84C] bg-[#C9A84C]/10" : "text-slate-300 bg-slate-100"}`}>
                      <i className="ri-star-fill text-sm leading-none" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Review Text</label>
                <textarea value={t.review} onChange={(e) => update(t.id, "review", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
